import { useEffect, useRef, useState } from "react";
import { Headphones, Loader2, Minus, Moon, Pause, Play, Plus, Sun } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { THEMES, ReaderPrefs } from "@/lib/deep";

const RATES = [0.85, 1, 1.25];

export function useChapterAudio(book: number, ch: number) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "playing" | "paused">("idle");
  const [rate, setRate] = useState(1);
  const [current, setCurrent] = useState<number | null>(null);
  useEffect(() => () => { const a = ref.current; if (a) { a.onpause = null; a.ontimeupdate = null; a.pause(); } ref.current = null; setState("idle"); setCurrent(null); }, [book, ch]);
  const timingsRef = useRef<{ n: number; start: number }[]>([]);
  const load = async () => {
    if (ref.current) return ref.current;
    setState("loading");
    const voice = localStorage.getItem("cast_voice") || "fable";
    try { timingsRef.current = (await api.get(`/deep/timings/${book}/${ch}`, { params: { voice } })).data.timings; } catch (e) { setState("idle"); toast.error(errMsg(e)); return null; }
    const a = new Audio(`${API}/deep/audio/${book}/${ch}?voice=${voice}`);
    a.onwaiting = () => setState("loading");
    a.onplaying = () => setState("playing");
    a.onpause = () => setState("paused");
    a.onended = () => { setState("idle"); setCurrent(null); };
    a.onerror = () => setState("idle");
    a.ontimeupdate = () => {
      const t = a.currentTime + 0.05;
      let n = 0;
      for (const s of timingsRef.current) { if (s.start <= t) n = s.n; else break; }
      setCurrent(n || null);
    };
    ref.current = a;
    return a;
  };
  const play = (a: HTMLAudioElement) => { a.playbackRate = rate; setState("loading"); a.play().catch(() => setState("idle")); };
  const toggle = async () => {
    const a = await load();
    if (!a) return;
    if (a.paused) play(a); else a.pause();
  };
  const playFrom = async (n: number) => {
    const a = await load();
    if (!a) return;
    const s = timingsRef.current.find((x) => x.n === n);
    if (s) a.currentTime = s.start;
    setCurrent(n);
    play(a);
  };
  const cycle = () => { const r = RATES[(RATES.indexOf(rate) + 1) % RATES.length]; setRate(r); if (ref.current) ref.current.playbackRate = r; };
  return { state, rate, toggle, cycle, current, playFrom };
}

export function ReaderBar({ prefs, setPrefs, audio, fg, line }: { prefs: ReaderPrefs; setPrefs: (p: ReaderPrefs) => void; audio: ReturnType<typeof useChapterAudio>; fg: string; line: string }) {
  const night = prefs.theme === "night";
  const btn = "flex h-10 items-center gap-1.5 rounded-full border px-3 text-sm font-semibold transition-colors";
  return (
    <div className="flex flex-wrap items-center gap-2" style={{ color: fg }} data-testid="reader-bar">
      <button data-testid="reader-narrate" onClick={audio.toggle} className={btn} style={{ borderColor: line }}>
        {audio.state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : audio.state === "playing" ? <Pause className="h-4 w-4" /> : audio.state === "paused" ? <Play className="h-4 w-4" /> : <Headphones className="h-4 w-4" />}
        <span data-testid="reader-narrate-label">{audio.state === "loading" ? "Preparing…" : audio.state === "playing" ? "Pause" : audio.state === "paused" ? "Resume" : "Listen"}</span>
      </button>
      {audio.state !== "idle" && <button data-testid="reader-rate" onClick={audio.cycle} className={btn} style={{ borderColor: line }}>{audio.rate}x</button>}
      <button data-testid="reader-daynight" aria-label="Day or night" onClick={() => setPrefs({ ...prefs, theme: night ? prefs.dayTheme : "night" })} className={btn} style={{ borderColor: line }}>
        {night ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}<span className="hidden sm:inline">{night ? "Day" : "Night"}</span>
      </button>
      <Popover>
        <PopoverTrigger asChild><button data-testid="reader-page-colors" className={btn} style={{ borderColor: line }}><span className="h-4 w-4 rounded-full border" style={{ background: THEMES.find((t) => t.id === prefs.theme)?.bg, borderColor: line }} />Page</button></PopoverTrigger>
        <PopoverContent className="w-64 space-y-4" align="end">
          <div className="grid grid-cols-5 gap-2">
            {THEMES.map((t) => (
              <button key={t.id} data-testid={`page-color-${t.id}`} onClick={() => setPrefs({ ...prefs, theme: t.id, dayTheme: t.night ? prefs.dayTheme : t.id })} className="flex flex-col items-center gap-1 text-[10px] font-semibold text-slate-600">
                <span className={`h-9 w-9 rounded-full border-2 ${prefs.theme === t.id ? "border-[#C86D51]" : "border-slate-200"}`} style={{ background: t.bg }} />{t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Text size</span>
            <span className="flex items-center gap-2">
              <button data-testid="text-smaller" onClick={() => setPrefs({ ...prefs, size: Math.max(16, prefs.size - 2) })} className="rounded-full border p-1.5"><Minus className="h-3.5 w-3.5" /></button>
              <span className="w-6 text-center text-sm" data-testid="text-size">{prefs.size}</span>
              <button data-testid="text-larger" onClick={() => setPrefs({ ...prefs, size: Math.min(30, prefs.size + 2) })} className="rounded-full border p-1.5"><Plus className="h-3.5 w-3.5" /></button>
            </span>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
